import React from "react";
import {
  Box,
  Stack,
  Paper,
  Group,
  Avatar,
  Text,
  TextInput,
  ActionIcon,
  Badge,
  Card,
  UnstyledButton,
} from "@mantine/core";
import {
  IconMicrophone,
  IconPlus,
  IconArrowLeft,
  IconClock,
  IconChevronRight,
  IconSparkles,
  IconRobot,
  IconMessageChatbot,
  IconGhost,
  IconSend,
} from "@tabler/icons-react";

export default function AssistantStyleLayout({
  messages,
  inputValue,
  setInputValue,
  handleSend,
  handleItemSelect,
  messagesEndRef,
  currentStep,
  config,
  activeTab,
  setActiveTab,
  previousChats,
}: any) {
  const theme = config?.theme || {};
  const messaging = config?.messaging || {};

  const renderBotIcon = (size = 28) => {
    const iconType = config?.botAvatarIcon || "robot";
    const iconColor = config?.botAvatarIconColor || "#ffffff";
    switch (iconType) {
      case "chatbot":
        return <IconMessageChatbot size={size} color={iconColor} />;
      case "sparkles":
        return <IconSparkles size={size} color={iconColor} />;
      case "ghost":
        return <IconGhost size={size} color={iconColor} />;
      default:
        return <IconRobot size={size} color={iconColor} />;
    }
  };

  return (
    <Box
      style={{
        background: `linear-gradient(135deg, ${theme.primaryColor || "#1e003b"} 0%, #050510 100%)`,
        height: "100%",
        display: "flex",
        flexDirection: "column",
        fontFamily: "Inter, sans-serif",
        color: "#ffffff",
      }}
    >
      {/* Header - Glassmorphism */}
      <Group
        position="apart"
        p="sm"
        px="md"
        style={{
          borderBottom: "1px solid rgba(255, 255, 255, 0.1)",
          backgroundColor: "rgba(255, 255, 255, 0.05)",
          backdropFilter: "blur(12px)",
          zIndex: 10,
        }}
      >
        <Group spacing="sm">
          {config?.companyLogoUrl && (
            <Avatar src={config.companyLogoUrl} size="sm" radius="md" />
          )}
          <Box>
            <Group spacing={6} align="center">
              <Text weight={700} size="sm" style={{ letterSpacing: "-0.2px" }}>
                {config?.companyName || "Bench AI"}
              </Text>
              <Badge
                size="xs"
                variant="dot"
                color="cyan"
                style={{
                  padding: 0,
                  border: "none",
                  background: "transparent",
                }}
              >
                Online
              </Badge>
            </Group>
            <Text size="11px" style={{ opacity: 0.6 }} weight={500}>
              High-Tech Gym Assistant
            </Text>
          </Box>
        </Group>
        <ActionIcon
          variant="transparent"
          style={{ color: "rgba(255,255,255,0.7)" }}
          onClick={() =>
            setActiveTab(activeTab === "current" ? "history" : "current")
          }
        >
          {activeTab === "current" ? (
            <IconClock size={16} />
          ) : (
            <IconChevronRight size={16} />
          )}
        </ActionIcon>
      </Group>

      {/* Chat Body */}
      <Stack p="md" style={{ flex: 1, overflowY: "auto" }} spacing="lg">
        {activeTab === "history" ? (
          <Box>
            <Text weight={700} size="sm" mb="md">
              Past Conversations
            </Text>
            {previousChats && previousChats.length > 0 ? (
              <Stack spacing="sm">
                {previousChats.map((chat: any, idx: number) => (
                  <Card
                    key={idx}
                    withBorder
                    radius="md"
                    p="sm"
                    style={{
                      backgroundColor: "rgba(255,255,255,0.05)",
                      borderColor: "rgba(255,255,255,0.1)",
                      color: "#fff",
                    }}
                  >
                    <Group position="apart" mb="xs">
                      <Text size="xs" weight={700}>
                        {new Date(chat.created_at).toLocaleDateString()}
                      </Text>
                      <Badge size="sm" color="cyan" variant="outline">
                        {chat.status || "Follow-up"}
                      </Badge>
                    </Group>
                    <Text
                      size="xs"
                      style={{ opacity: 0.8, whiteSpace: "pre-wrap" }}
                    >
                      {chat.description}
                    </Text>
                  </Card>
                ))}
              </Stack>
            ) : (
              <Text size="xs" color="dimmed">
                No past conversations found for this number.
              </Text>
            )}
          </Box>
        ) : (
          <Box>
            {/* Welcome Avatar Box - Elevated Minimalist Greeting */}
            {messages.length <= 1 && (
              <Box
                style={{
                  textAlign: "center",
                  paddingTop: "24px",
                  paddingBottom: "16px",
                }}
              >
                <Avatar
                  size={64}
                  radius={24}
                  mx="auto"
                  src={undefined}
                  style={{
                    boxShadow:
                      "0 10px 25px -5px rgba(0, 0, 0, 0.1), 0 8px 10px -6px rgba(0, 0, 0, 0.1)",
                    border: "2px solid #fff",
                    background:
                      config?.botAvatarBgColor ||
                      "linear-gradient(135deg, #111 0%, #333 100%)",
                  }}
                >
                  {renderBotIcon(28)}
                </Avatar>
                <Text
                  weight={800}
                  size="lg"
                  mt="md"
                  style={{ letterSpacing: "-0.3px" }}
                >
                  Hello! 👋
                </Text>
                <Text size="xs" color="dimmed" mt={2}>
                  What would you like to achieve today?
                </Text>
              </Box>
            )}

            {/* Chat History Flow */}
            <Box>
              <Group position="center" mb="md">
                <Badge
                  size="xs"
                  variant="subtle"
                  color="gray"
                  style={{
                    textTransform: "none",
                    fontWeight: 500,
                    letterSpacing: "0.2px",
                  }}
                >
                  Conversation History
                </Badge>
              </Group>

              <Stack spacing="md">
                {messages.map((msg: any) => (
                  <Box key={msg.id}>
                    {msg.sender === "user" ? (
                      <Paper
                        p="sm"
                        radius="lg"
                        style={{
                          backgroundColor: "rgba(255, 255, 255, 0.2)",
                          backdropFilter: "blur(10px)",
                          color: "#fff",
                          marginLeft: "auto",
                          maxWidth: "80%",
                          width: "fit-content",
                          marginTop: 8,
                          marginBottom: 12,
                          border: "1px solid rgba(255,255,255,0.1)",
                          borderBottomRightRadius: "4px",
                        }}
                      >
                        <Text size="xs" style={{ lineHeight: 1.4 }}>
                          {msg.text}
                        </Text>
                      </Paper>
                    ) : (
                      <Group align="flex-start" spacing="sm" mb="md">
                        <Avatar
                          radius="xl"
                          size="sm"
                          src={undefined}
                          style={{
                            boxShadow: "0 2px 5px rgba(0,0,0,0.1)",
                            background:
                              config?.botAvatarBgColor ||
                              "linear-gradient(135deg, #111 0%, #333 100%)",
                            color: config?.botAvatarIconColor || "#fff",
                          }}
                        >
                          {renderBotIcon(16)}
                        </Avatar>
                        <Box style={{ maxWidth: "82%" }}>
                          <Paper
                            p="sm"
                            radius="lg"
                            style={{
                              backgroundColor: "rgba(0, 0, 0, 0.3)",
                              backdropFilter: "blur(10px)",
                              color: "#fff",
                              border: "1px solid rgba(255, 255, 255, 0.1)",
                              boxShadow: "0 4px 15px rgba(0, 0, 0, 0.2)",
                              borderTopLeftRadius: "4px",
                            }}
                          >
                            {msg.id === 1 && (
                              <Text
                                size="xs"
                                weight={700}
                                mb={4}
                                style={{
                                  color: theme.primaryColor || "#00e5ff",
                                }}
                              >
                                Hi! I'm Bench AI ✨
                              </Text>
                            )}
                            <Text size="xs" style={{ lineHeight: 1.5 }}>
                              {msg.text}
                            </Text>
                          </Paper>

                          {/* Options as Badges/Chips (Modern Pill style) */}
                          {msg.options && (
                            <Group spacing={6} mt="xs">
                              {msg.options.map((opt: string) => (
                                <Badge
                                  key={opt}
                                  variant="outline"
                                  color="dark"
                                  size="lg"
                                  radius="xl"
                                  style={{
                                    cursor: "pointer",
                                    textTransform: "none",
                                    fontWeight: 500,
                                    backgroundColor: "#fff",
                                    borderColor: "rgba(0,0,0,0.12)",
                                    transition: "all 0.2s ease",
                                    boxShadow: "0 1px 2px rgba(0,0,0,0.02)",
                                    height: "auto",
                                    minHeight: "34px",
                                    whiteSpace: "normal",
                                    padding: "8px 14px",
                                    lineHeight: 1.4,
                                    maxWidth: "100%",
                                  }}
                                  onClick={() => handleSend(opt)}
                                >
                                  {opt}
                                </Badge>
                              ))}
                            </Group>
                          )}

                          {/* List Items */}
                          {msg.listItems && (
                            <Stack spacing="xs" mt="sm">
                              {msg.listItems.map((item: any, idx: number) => (
                                <Card
                                  key={idx}
                                  withBorder
                                  radius="md"
                                  p="sm"
                                  style={{
                                    cursor: "pointer",
                                    backgroundColor: "#fff",
                                    borderColor: "rgba(0, 0, 0, 0.08)",
                                    transition: "all 0.2s ease",
                                    boxShadow: "0 2px 6px rgba(0,0,0,0.02)",
                                  }}
                                  onClick={() => handleItemSelect(item)}
                                >
                                  <Group position="apart">
                                    <Box>
                                      <Text
                                        size="xs"
                                        weight={700}
                                        style={{ letterSpacing: "-0.1px" }}
                                      >
                                        {item.title}
                                      </Text>
                                      <Text size="11px" color="dimmed" mt={2}>
                                        {item.subtitle}
                                      </Text>
                                    </Box>
                                    <ActionIcon
                                      variant="light"
                                      color="gray"
                                      radius="xl"
                                      size="sm"
                                    >
                                      <IconChevronRight size={12} />
                                    </ActionIcon>
                                  </Group>
                                </Card>
                              ))}
                            </Stack>
                          )}
                        </Box>
                      </Group>
                    )}
                  </Box>
                ))}
              </Stack>
            </Box>
            <div ref={messagesEndRef} />
          </Box>
        )}
      </Stack>

      {/* Glassmorphism Footer Input */}
      {activeTab === "current" &&
        (currentStep === "ASK_CONTACT" ||
          currentStep === "ASK_ANYTHING_ELSE") && (
          <Box
            p="sm"
            style={{
              borderTop: "1px solid rgba(255, 255, 255, 0.1)",
              backgroundColor: "rgba(255, 255, 255, 0.05)",
              backdropFilter: "blur(12px)",
            }}
          >
            <TextInput
              placeholder="Ask Bench AI anything..."
              radius="xl"
              size="md"
              style={{ flex: 1 }}
              value={inputValue}
              onChange={(e) => setInputValue(e.currentTarget.value)}
              onKeyPress={(e) => e.key === "Enter" && handleSend()}
              styles={{
                input: {
                  backgroundColor: "rgba(0, 0, 0, 0.2)",
                  border: "1px solid rgba(255, 255, 255, 0.1)",
                  color: "#fff",
                  fontSize: "13px",
                  paddingLeft: "16px",
                  "&:focus": {
                    borderColor: theme.primaryColor || "#00e5ff",
                    backgroundColor: "rgba(0, 0, 0, 0.4)",
                  },
                },
              }}
              rightSection={
                <ActionIcon
                  onClick={() => handleSend()}
                  variant="filled"
                  radius="xl"
                  size="sm"
                  style={{
                    backgroundColor: theme.primaryColor || "#00e5ff",
                    marginRight: 4,
                    boxShadow: "0 0 10px rgba(0, 229, 255, 0.5)",
                  }}
                >
                  <IconSend size={14} color="#000" />
                </ActionIcon>
              }
            />
          </Box>
        )}
    </Box>
  );
}
